import {
  CancellationToken,
  createCancellationTokenSource,
} from 'infra/cancellation'
import memoize from 'memoizee'
import {
  asyncScheduler,
  combineLatest,
  defer,
  EMPTY,
  from,
  interval,
  merge,
  MonoTypeOperatorFunction,
  Observable,
  ObservableInput,
  of,
  Subscription,
  ObservedValueUnionFromArray,
  BehaviorSubject,
  timer,
} from 'rxjs'
import {
  distinctUntilChanged,
  endWith,
  filter,
  find,
  map,
  shareReplay,
  startWith,
  switchMap,
  share,
  takeWhile,
} from 'rxjs/operators'
import { isNotNil } from './object-util'
import { match, DEFAULT } from './pattern-match-util'

/**
 * RxJS filter for filtering out nil values.
 */
export function filterNonNil<T>() {
  return (source$: Observable<T | null | undefined>) =>
    source$.pipe(filter(isNotNil))
}

/**
 * Returns an Observable that emits the initial or latest value on subscribe,
 * and also makes sure only a single subscription is made to the source.
 *
 * This means that **anything that goes before this operator** is triggered only once for
 * concurrent subscriptions.
 *
 * @param initialValue
 */
export function shareBehavior<T>(initialValue: T) {
  return (source$: Observable<T>) =>
    source$.pipe(
      share({
        connector: () => new BehaviorSubject(initialValue),
        resetOnError: false,
        resetOnComplete: false,
        resetOnRefCountZero: false,
      }),
    )
}

/**
 * Shortcut for shareReplay with ref-counting and a buffer size of 1.
 */
export function shareLatest<T>() {
  return (source$: Observable<T>) =>
    source$.pipe(shareReplay({ refCount: true, bufferSize: 1 }))
}

/**
 * Distinct but with a cache size.
 *
 * @param cacheSize
 * @param keySelector
 */
export function distinctCount<T>(
  cacheSize: number,
  keySelector?: (v: T) => any,
) {
  return (source$: Observable<T>): Observable<T> => {
    return new Observable<T>((observer) => {
      return source$.subscribe({
        error: observer.error.bind(observer),
        complete: observer.complete.bind(observer),
        next: memoize(observer.next.bind(observer), {
          max: cacheSize,
          normalizer: keySelector ? (args) => keySelector(args[0]!) : undefined,
        }),
      })
    })
  }
}

/**
 * When `signal$` emits `true`, values flow through. When `false`, they queue up, and get flushed
 * when it emits `true` again. If `signal$` completes, flow is enabled.
 *
 * @param signal$
 * @param initial
 */
export function flowToggle<T>(signal$: Observable<boolean>, initial = false) {
  return (source$: Observable<T>) =>
    new Observable<T>((observer) => {
      let queue: T[] = []
      let flowing = initial
      const signalSub = signal$.subscribe({
        next: (shouldFlow) => {
          flowing = shouldFlow
          if (flowing) {
            flush()
          }
        },
        error: observer.error.bind(observer),
        complete: () => {
          flowing = true
          flush()
        },
      })

      const sourceSub = source$.subscribe({
        error: observer.error.bind(observer),
        complete: () => {
          flush()
          observer.complete()
        },
        next: (value) => {
          if (flowing) {
            return observer.next(value)
          }
          queue.push(value)
        },
      })

      return () => {
        signalSub.unsubscribe()
        sourceSub.unsubscribe()
      }

      function flush() {
        queue.forEach((v) => observer.next(v))
        queue = []
      }
    })
}

/**
 * Creates an interval that emits the current time initially and every second.
 */
export function fromClock(
  /* istanbul ignore next */
  getNow = () => new Date(),
): Observable<Date> {
  return interval(1000).pipe(
    startWith(0),
    map(() => getNow()),
  )
}

/**
 * Calls `factory` with a cancellation token to get an observable input and subscribes to it, while
 * managing the cancellation token. Cancellations are not thrown as errors.
 */
export function withCancellation<T>(
  factory: (ct: CancellationToken) => ObservableInput<T>,
) {
  return new Observable<T>((subscriber) => {
    let cancellation = createCancellationTokenSource()
    const subscription = from(factory(cancellation.token)).subscribe({
      complete: subscriber.complete.bind(subscriber),
      error: subscriber.error.bind(subscriber),
      next: (value) => {
        subscriber.next(value)
      },
    })
    return () => {
      cancellation.cancel()
      cancellation = createCancellationTokenSource()
      subscription.unsubscribe()
    }
  })
}

/**
 * Takes an observable of pairs and returns an observable for the first element
 * from the pair.
 */
export function unpair<T extends any[]>() {
  return (source$: Observable<T>): Observable<T[0]> =>
    source$.pipe(map(([x]) => x))
}

/**
 * Like a combination of `switchMap` and `exhaustMap`, but when a new source value is emitted, ignores values from the
 * inner observable and waits for it to complete, then subscribes to the last queued item.
 *
 * Useful for setting up syncing pipelines (spam save requests, always be saving but only one at a time and only the last one).
 *
 * source:     --1---------2--3|
 * project(1): --(a1)--(b1)------(c1)|
 * project(2):  (never subscribed)
 * project(3):                       (a3)---(b3)---(c3)--|
 * output:     --(a1)--(b1)----------(a3)---(b3)---(d3)--|
 *
 */
export function tailMap<T, R>(
  project: (item: T, index: number) => ObservableInput<R>,
  resultSelector = (item: R): R => item,
) {
  return (source$: Observable<T>): Observable<R> =>
    new Observable<R>((subscriber) => {
      let sourceCompleted = false
      let inProgress = false
      let hasQueued = false
      let queuedValue: T | undefined
      let index = 0
      let innerSub = new Subscription()
      const sub = new Subscription()
      sub.add(
        source$.subscribe({
          next: onSourceNext,
          error: subscriber.error.bind(subscriber),
          complete: onSourceComplete,
        }),
      )
      return sub

      /**
       * Handler for when the source emits a value.
       *
       * @param value
       */
      function onSourceNext(value: T) {
        if (inProgress) {
          hasQueued = true
          queuedValue = value
          return
        }

        next(value)
      }

      /**
       * Starts the next subscription.
       *
       * @param value
       */
      function next(value: T) {
        inProgress = true
        innerSub.unsubscribe()
        sub.remove(innerSub)
        // defer handles errors too
        innerSub = defer(() => project(value, index++)).subscribe({
          next: onInnerNext,
          complete: onInnerComplete,
          error: onInnerError,
        })
        sub.add(innerSub)
      }

      /**
       * When the inner observable emits, forward it if it is the tail.
       *
       * @param value
       */
      function onInnerNext(value: R) {
        // If this isn't the tail, don't emit.
        if (!hasQueued) {
          subscriber.next(resultSelector(value))
        }
      }

      /**
       * When the inner observable errors, forward it if it is the tail.
       *
       * @param error
       */
      function onInnerError(error: unknown) {
        // If this the tail, forward the error.
        if (!hasQueued) {
          subscriber.error(error)
        } else {
          // If it's not, ignore the error and process the tail item.
          clearAndEmitTail()
        }
      }

      /**
       * When the source completes, if we haven't mapped anything yet,
       * complete the stream.
       */
      function onSourceComplete() {
        if (!inProgress) {
          subscriber.complete()
        }
        sourceCompleted = true
      }

      /**
       * When the inner observable completes, if the source has completed,
       * complete the observable.
       */
      function onInnerComplete() {
        inProgress = false
        if (hasQueued) {
          clearAndEmitTail()
          return
        }

        if (sourceCompleted) {
          subscriber.complete()
        }
      }

      /**
       * Emits the tail value and clears it.
       */
      function clearAndEmitTail() {
        hasQueued = false
        const value = queuedValue!
        queuedValue = undefined
        next(value)
      }
    })
}

/**
 * Subscribes to the source when `isActive$` emits `true`, unsubscribes
 * when `false`. When inactive, the returned `Observable` will never complete.
 *
 * @param isActive$ The observable that controls whether we are
 *  subscribing or not.
 */
export function whileActive<T>(
  isActive$: Observable<boolean>,
): MonoTypeOperatorFunction<T> {
  return (source$) =>
    isActive$.pipe(
      distinctUntilChanged(),
      switchMap((isActive) => (isActive ? source$ : EMPTY)),
    )
}

/**
 * Mirrors the source but also emits the latest item from source when the
 * signal emits.
 *
 * @param signal$
 */
export function emitLatestWhen<T>(
  signal$: Observable<unknown>,
): MonoTypeOperatorFunction<T> {
  return (source$) =>
    source$.pipe(
      switchMap((item) => merge(of(item), signal$.pipe(map(() => item)))),
    )
}

/**
 * Returns a Cancellation Token that cancels either when the observable
 * emits a value that passes the specified predicate, or when it completes.
 *
 * If a predicate is not provided, it cancels when the first item emits.
 *
 * @param source$
 * @param predicate
 */
export function cancelWhen<T>(
  source$: Observable<T>,
  predicate?: (item: T) => boolean,
) {
  const cts = createCancellationTokenSource()
  const sub = source$.pipe(find(predicate || (() => true))).subscribe({
    error: cts.cancel,
    complete: cts.cancel,
  })
  cts.onDispose(() => sub.unsubscribe())
  return cts
}

/**
 * Returns a Cancellation Token that cancels either when the input observable
 * emits `false` or when it completes.
 *
 * @param active$
 */
export function cancelOnDeactivate(active$: Observable<boolean>) {
  return cancelWhen(active$, (v) => !v)
}

/**
 * Batches elements based on time or max elements, whichever comes first.
 *
 * @param waitTimeSpan The amount of time to wait for elements before we emit the batch
 * @param maxElementsPerBatch If the batch size reaches this number, emit it without waiting. Defaults to infinity.
 * @param scheduler The scheduler to use.
 */
export function batchElements<T>(
  waitTimeSpan: number,
  maxElementsPerBatch = Number.POSITIVE_INFINITY,
  scheduler = asyncScheduler,
) {
  return (source$: Observable<T>): Observable<T[]> =>
    new Observable((subscriber) => {
      let currentBatch: T[] = []
      let scheduledFlush: Subscription | undefined
      let completed = false
      const subscription = source$.subscribe({
        error(err) {
          subscriber.error(err)
          clearSchedule()
        },
        next(value) {
          currentBatch.push(value)
          if (currentBatch.length >= maxElementsPerBatch) {
            maybeFlushBatch()
            return
          }

          scheduledFlush ??= scheduler.schedule(maybeFlushBatch, waitTimeSpan)
        },
        complete() {
          completed = true
          maybeFlushBatch()
        },
      })

      /**
       * Flushes the batch if there is one.
       * If we received a completion signal from the source,
       * complete the observable afterwards.
       */
      function maybeFlushBatch() {
        const batch = currentBatch
        currentBatch = []
        clearSchedule()
        if (batch.length > 0) {
          subscriber.next(batch)
        }

        if (completed) {
          subscriber.complete()
        }
      }

      /**
       * Clears any scheduled item.
       */
      function clearSchedule() {
        scheduledFlush?.unsubscribe()
        scheduledFlush = undefined
      }

      return () => {
        subscription.unsubscribe()
      }
    })
}

/**
 * Creation operator that turns the input array of observables
 * into an observable that emits `true` while all conditions are met
 * and `false` when they don't.
 *
 * Uses `combineLatest` and `distinctUntilChanged`.
 *
 * @param inputs
 * @param predicate
 */
export function every<T extends ObservableInput<any>[]>(
  inputs: T,
  predicate: (value: ObservedValueUnionFromArray<T>) => boolean = Boolean,
): Observable<boolean> {
  return combineLatest(inputs).pipe(
    map((conditions) => conditions.every(predicate)),
    distinctUntilChanged(),
  )
}

/**
 * Creation operator that turns the input array of observables
 * into an observable that emits `true` while some conditions are met
 * and `false` when none do.
 *
 * Uses `combineLatest` and `distinctUntilChanged`.
 *
 * @param inputs
 * @param predicate
 */
export function some<T extends ObservableInput<any>[]>(
  inputs: T,
  predicate: (value: ObservedValueUnionFromArray<T>) => boolean = Boolean,
): Observable<boolean> {
  return combineLatest(inputs).pipe(
    map((conditions) => conditions.some(predicate)),
    distinctUntilChanged(),
  )
}

/**
 * Executes `callback` when unsubscribing.
 *
 * @param callback
 */
export function teardown<T>(callback: () => void) {
  return (source: Observable<T>) =>
    new Observable<T>((observer) => {
      const subscription = source.subscribe(observer)
      return () => {
        callback()
        subscription.unsubscribe()
      }
    })
}

/**
 * Emits an observable countdown of seconds remaining until the target date.
 */
export function countdown(target: Date): Observable<number> {
  const difference = differenceBetweenNowAndTargetInSeconds(target)

  return timer(delayToRoundSecond(new Date()), 1000).pipe(
    map((x: number) => difference - x),
    takeWhile((x) => x > 0),
    startWith(difference),
    endWith(0),
  )

  function delayToRoundSecond(date: Date) {
    return 1000 - date.getMilliseconds()
  }

  function differenceBetweenNowAndTargetInSeconds(date: Date) {
    const millisecondDifference = Math.round(
      date.getTime() - new Date().getTime(),
    )

    return millisecondDifference / 1000
  }
}

/**
 * Indicates the interval upon which the clock should tick.
 */
export enum TickEvery {
  Second = 'second',
  Minute = 'minute',
  Hour = 'hour',
}

/**
 * A clock observable. Ticks at the specified interval. If you specify
 * a well-defined `TickEvery` enum, ticks are synchronized with clock time.
 *
 * If you provide an arbitrary millisecond interval, ticks are aligned
 * only to the nearest whole second.
 */
export function clock(tickEvery: TickEvery | number) {
  const intervalDuration = !isTickEveryEnum(tickEvery)
    ? tickEvery
    : match(tickEvery, {
        [TickEvery.Second]: () => 1000,
        [TickEvery.Minute]: () => 60_000,
        [TickEvery.Hour]: () => 60_000_000,
      })

  return timer(delayToRoundStartTime(new Date()), intervalDuration).pipe(
    map(() => new Date()),
    startWith(new Date()),
  )

  /**
   * Computes the delay to ensure a round start time in sync with the real time.
   */
  function delayToRoundStartTime(date: Date) {
    return match(tickEvery, {
      [TickEvery.Second]: () => 1000 - date.getMilliseconds(),
      [TickEvery.Minute]: () => (60 - date.getSeconds()) * 1000,
      [TickEvery.Hour]: () => (60 - date.getMinutes()) * 60 * 1000,

      // Handles the arbitrary intervals.
      [DEFAULT]: () => 1000 - date.getMilliseconds(),
    })
  }

  function isTickEveryEnum(value: any): value is TickEvery {
    return value === 'second' || value === 'minute' || value === 'hour'
  }
}
